import React, { useCallback, useEffect, useState } from 'react';
import { Wand2, Loader2, Check, AlertTriangle, ShieldQuestion, ArrowRight, X, Undo2, LifeBuoy } from 'lucide-react';
import { autonomousApi, onPlanEvent, onPlanChange } from './autonomousApi';
import { dispatchCoachAction } from './coachActionBus';
import HighlightLayer from './HighlightLayer';
import CoachChartPreview from './CoachChartPreview';

const RISK_CLS = {
  LOW: 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10',
  MEDIUM: 'text-amber-400 border-amber-500/40 bg-amber-500/10',
  HIGH: 'text-red-400 border-red-500/40 bg-red-500/10',
};

// Detect a chart type from the user's goal text for the illustrative preview.
const detectChart = (text) => {
  const m = String(text || '').toLowerCase().match(/\b(bar|line|area|pie)\b/);
  return m ? m[1] : null;
};

// Autonomous Coach experience: type a goal → the server generates + VALIDATES a
// plan → step through it with live SERVER-SIDE verification. The panel never
// decides completion; it reflects the server's decision on each forwarded event.
export default function AutonomousPanel({ onHandoff }) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // { plan, validation, blocked }
  const [session, setSession] = useState(null); // { sid, step, status, total_steps }
  const [banner, setBanner] = useState('');
  const [deviation, setDeviation] = useState('');
  const [needsReplan, setNeedsReplan] = useState(false);
  const [undo, setUndo] = useState(null);       // { available, stepId, hint }
  const [confirmed, setConfirmed] = useState(false);
  const [acting, setActing] = useState(false);
  const [chartType, setChartType] = useState(null);

  // Reflect the server's decision on each forwarded event.
  useEffect(() => onPlanEvent((d) => {
    if (!d) return;
    setSession((prev) => {
      if (!prev) return prev;
      const next = { ...prev, status: d.status };
      if (d.step) next.step = d.step;
      return next;
    });
    if (d.undo && d.undo.available) setUndo(d.undo);
    if (d.advanced || d.completed) setConfirmed(false);
    if (d.completed || d.status === 'completed') { setBanner('Goal complete — verified by the server.'); setDeviation(''); setNeedsReplan(false); }
    else if (d.needs_confirmation) setDeviation('');
    else if (d.deviation) {
      setNeedsReplan(!!d.needs_replan);
      setDeviation(d.needs_replan
        ? 'This keeps diverging from the plan.'
        : 'That did not match the current step — follow the highlighted instruction to continue.');
    } else if (d.advanced) { setDeviation(''); setNeedsReplan(false); }
  }), []);

  // If the plan session is cleared elsewhere (completed/cancelled), clear ours.
  useEffect(() => onPlanChange((p) => { if (!p) setSession((s) => (s && s.status !== 'completed' ? null : s)); }), []);

  // Re-hydrate the visible run when this panel remounts (e.g. switching the
  // mode tab, reopening the Coach, OR after a hard page reload — the plan
  // tracker is persisted to localStorage). A COMPLETED session restores its
  // Undo affordance; a stale/expired session (404) is cleared.
  useEffect(() => {
    const p = autonomousApi.getActive();
    if (!p) return;
    autonomousApi.step(p.session_id)
      .then((r) => {
        if (!r) return;
        autonomousApi.reconcile(r.seq);
        if (r.status === 'completed') {
          setSession({ sid: p.session_id, step: null, status: 'completed', total_steps: undefined });
          setBanner('Goal complete — verified by the server.');
          if (r.undo && r.undo.available) setUndo(r.undo);
        } else if (r.step) {
          setSession({ sid: p.session_id, step: r.step, status: r.status, total_steps: r.step.total });
        } else {
          autonomousApi.end();
        }
      })
      .catch(() => { autonomousApi.end(); });
  }, []);

  const doPreview = async (e) => {
    e?.preventDefault?.();
    const text = q.trim();
    if (!text) return;
    setBusy(true); setPreview(null); setBanner('');
    setChartType(detectChart(text));
    try { setPreview(await autonomousApi.preview(text)); }
    catch (err) { setBanner(err.message || 'Could not build a plan.'); }
    finally { setBusy(false); }
  };

  const doStart = async (confirm) => {
    setBusy(true); setBanner('');
    setChartType(detectChart(q.trim()));
    try {
      const r = await autonomousApi.start(q.trim(), confirm);
      if (r.session_id) {
        setSession({ sid: r.session_id, step: r.step, status: r.status, total_steps: r.total_steps });
        setPreview(null); setDeviation(''); setUndo(null); setConfirmed(false); setNeedsReplan(false);
      } else if (r.needs_confirmation) {
        setPreview((p) => ({ ...(p || {}), plan: r.plan, validation: r.validation, needs_confirmation: true }));
      } else {
        setBanner(blockedMessage(r.blocked, r.validation));
      }
    } catch (err) { setBanner(err.message || 'Could not start the plan.'); }
    finally { setBusy(false); }
  };

  const cancel = useCallback(async () => {
    const p = autonomousApi.getActive();
    if (p) { try { await autonomousApi.control(p.session_id, 'cancel'); } catch (e) { /* ignore */ } }
    autonomousApi.end();
    setSession(null); setBanner(''); setDeviation(''); setUndo(null); setConfirmed(false); setNeedsReplan(false); setChartType(null);
  }, []);

  // Phase 6: confirm the reversible step, then perform the UI-state action.
  const doConfirm = async () => {
    const p = autonomousApi.getActive();
    if (!p || !session?.step) return;
    setActing(true);
    try {
      await autonomousApi.confirm(p.session_id, session.step.stepId);
      setConfirmed(true);
      const handled = dispatchCoachAction({ type: 'apply_filter', capabilityId: session.step.capabilityId, parameters: session.step.parameters });
      if (!handled) setBanner('Open a report view to apply the filter — it will complete automatically.');
    } catch (e) { setBanner(e.message || 'Could not confirm.'); }
    finally { setActing(false); }
  };

  const doUndo = async () => {
    const p = autonomousApi.getActive();
    if (!p || !undo) return;
    setActing(true);
    try {
      const r = await autonomousApi.undo(p.session_id, undo.stepId);
      dispatchCoachAction({ type: 'undo_filter', stepId: undo.stepId });
      setUndo(null); setConfirmed(false); setBanner('');
      setSession((prev) => (prev ? { ...prev, step: r.step, status: r.status } : prev));
    } catch (e) { setBanner(e.message || 'Could not undo.'); }
    finally { setActing(false); }
  };

  const doHandoff = async () => {
    const goalText = q.trim() || session?.step?.name || '';
    await cancel();
    if (onHandoff) onHandoff(goalText);
  };

  // ── Active plan run ──
  if (session) {
    const step = session.step || {};
    const done = session.status === 'completed';
    const needsConfirm = step.requiresConfirmation && !confirmed && !done;
    return (
      <>
      {!done && step.targetId && <HighlightLayer targetId={step.targetId} />}
      <div className="space-y-3" data-testid="autonomous-run">
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">
            Autonomous plan · step {(step.index ?? 0) + 1} of {step.total ?? session.total_steps}
          </span>
          <button data-testid="autonomous-cancel" onClick={cancel} aria-label="Cancel plan"
            className="text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 rounded">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        {done ? (
          <div data-testid="autonomous-complete" className="space-y-2">
            <div className="text-sm text-emerald-400 flex items-center gap-2" role="status">
              <Check className="w-4 h-4" /> {banner || 'Goal complete — verified by the server.'}
            </div>
            {undo && undo.available && (
              <button data-testid="autonomous-undo" disabled={acting} onClick={doUndo}
                className="inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1 rounded-lg border border-border/60 hover:bg-muted/50 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary/40">
                <Undo2 className="w-3.5 h-3.5" /> Undo{undo.hint ? ` — ${undo.hint}` : ''}
              </button>
            )}
          </div>
        ) : (
          <div data-testid="autonomous-step">
            <div className="text-sm font-medium">{step.name}</div>
            <p className="text-[13px] text-muted-foreground mt-0.5" aria-live="polite">{step.instruction}</p>

            {needsConfirm ? (
              <div className="mt-2" data-testid="autonomous-confirm">
                <p className="text-[11px] text-amber-400 flex items-start gap-1">
                  <ShieldQuestion className="w-3 h-3 mt-0.5 shrink-0" /> The Coach can do this for you — a reversible action you can undo.
                </p>
                <button data-testid="autonomous-confirm-btn" disabled={acting} onClick={doConfirm}
                  className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary/40">
                  {acting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Confirm &amp; apply
                </button>
              </div>
            ) : confirmed ? (
              <p className="mt-2 text-[11px] text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Applying…</p>
            ) : (
              <p className="mt-2 text-[11px] text-muted-foreground">Do the highlighted action in the app — the Coach verifies each step as you go.</p>
            )}

            {deviation && (
              <p data-testid="autonomous-deviation" className="mt-2 text-[11px] text-amber-400 flex items-start gap-1" role="status">
                <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {deviation}
              </p>
            )}
            {needsReplan && (
              <button data-testid="autonomous-handoff" disabled={acting} onClick={doHandoff}
                className="mt-2 inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg border border-primary/40 text-primary hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary/40">
                <LifeBuoy className="w-3.5 h-3.5" /> Get a guided walkthrough instead
              </button>
            )}
          </div>
        )}
        {chartType && !needsReplan && (
          <CoachChartPreview
            chartType={chartType}
            progress={done ? 1 : Math.min(1, (step.index ?? 0) / Math.max(1, (step.total ?? session.total_steps ?? 2) - 1))}
          />
        )}
      </div>
      </>
    );
  }

  // ── Plan preview (validated, not yet started) ──
  if (preview && preview.plan) {
    const v = preview.validation || {};
    const risk = preview.plan.risk || 'LOW';
    const needsConfirm = preview.needs_confirmation || v.requires_confirmation;
    return (
      <div className="space-y-3" data-testid="autonomous-preview">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">{preview.plan.goal?.desiredOutcome || 'Suggested plan'}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${RISK_CLS[risk] || RISK_CLS.LOW}`} data-testid="autonomous-risk">{risk}</span>
        </div>
        <ol className="space-y-1.5" data-testid="autonomous-steps">
          {(preview.plan.steps || []).map((s, i) => (
            <li key={s.stepId} className="text-[13px] flex items-start gap-2">
              <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full bg-primary/15 text-primary text-[10px] flex items-center justify-center">{i + 1}</span>
              <span>{s.name || s.capabilityId.split('.').slice(1).join(' ') || s.capabilityId}</span>
            </li>
          ))}
        </ol>
        {chartType && <CoachChartPreview chartType={chartType} progress={0.4} />}
        {needsConfirm && (
          <p className="text-[11px] text-amber-400 flex items-start gap-1" data-testid="autonomous-confirm-notice">
            <ShieldQuestion className="w-3 h-3 mt-0.5 shrink-0" /> This plan includes a {risk.toLowerCase()}-risk action — confirm to begin.
          </p>
        )}
        <div className="flex items-center gap-2">
          <button data-testid="autonomous-start" disabled={busy} onClick={() => doStart(!!needsConfirm)}
            className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary/40">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
            {needsConfirm ? 'Confirm & start' : 'Start plan'}
          </button>
          <button onClick={() => setPreview(null)} className="text-[11px] text-muted-foreground hover:text-foreground">Back</button>
        </div>
      </div>
    );
  }

  // ── Goal entry ──
  return (
    <div className="space-y-3" data-testid="autonomous-entry">
      <form onSubmit={doPreview} className="space-y-1.5">
        <label htmlFor="autonomous-input" className="text-[11px] text-muted-foreground">What do you want to accomplish?</label>
        <div className="flex items-center gap-1.5">
          <input
            id="autonomous-input"
            data-testid="autonomous-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="e.g. report of written premium by state as a bar chart"
            className="flex-1 text-[13px] px-2.5 py-1.5 rounded-lg border border-border/60 bg-background focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
          <button data-testid="autonomous-plan-btn" type="submit" disabled={busy} aria-label="Build a plan"
            className="h-8 w-8 flex items-center justify-center rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary/40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
          </button>
        </div>
      </form>
      {(preview && preview.blocked) && (
        <p data-testid="autonomous-blocked" className="text-[11px] text-amber-400 flex items-start gap-1" role="status">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {blockedMessage(preview.blocked, preview.validation)}
        </p>
      )}
      {banner && <p data-testid="autonomous-banner" className="text-[11px] text-muted-foreground">{banner}</p>}
    </div>
  );
}

// Honest, user-facing message for every non-plan outcome — never leaks internals.
const _PARAM_LABELS = { measureField: 'a measure (e.g. premium)', categoryField: 'a dimension (e.g. state)', field: 'a field to filter on' };
function blockedMessage(blocked, validation) {
  if (!blocked) {
    const errs = (validation && validation.errors) || [];
    return errs.length ? `This plan can't run: ${errs[0].message || errs[0].code || 'not permitted'}.` : 'No plan available.';
  }
  const code = blocked.code || '';
  if (code === 'no_capability_for_goal') return "I can't do that one yet — try a reporting or dashboard task.";
  if (code === 'missing_parameters' || code === 'needs_clarification' || (blocked.missing || []).length || (blocked.parameters || []).length) {
    const wanted = (blocked.parameters || blocked.missing || []).map((p) => _PARAM_LABELS[p] || p);
    return wanted.length ? `I need a bit more detail — add ${wanted.join(' and ')}.` : 'I need a bit more detail — please rephrase your goal.';
  }
  if (code === 'missing_permission') return "You don't have permission for part of this task.";
  if (code === 'plan_rejected') return "That plan didn't pass validation, so I won't run it.";
  return blocked.message || "I can't run that request.";
}
